import { ReactNode } from 'react';
import {
    TbBraces,
    TbFileText,
    TbFileTypeCss,
    TbFileTypeCsv,
    TbFileTypeDocx,
    TbFileTypeHtml,
    TbFileTypeJpg,
    TbFileTypeJs,
    TbFileTypePdf,
    TbFileTypePng,
    TbFileTypePpt,
    TbFileTypeSvg,
    TbFileTypeTs,
    TbFileTypeTxt,
    TbFileTypeXls,
    TbFileTypeXml,
    TbFileTypeZip,
    TbMarkdown,
} from 'react-icons/tb';

import {
    GoogleDocsSVGIcon,
    GoogleFormsSVGIcon,
    GoogleSheetsSVGIcon,
    GoogleSlidesSVGIcon,
} from '@/assets';
import { imgLogoStyling } from '@/theme';



import type { MapToken } from './types';

export const createFileIconMaps = (token: MapToken) => {
    const fileExtensionToMimeTypeMap: Record<string, string> = {
        pdf: 'application/pdf',
        doc: 'application/msword',
        docx: 'application/vnd.openxmlformats-officedocument.wordprocessingml.document',
        rtf: 'application/rtf',
        odt: 'application/vnd.oasis.opendocument.text',
    
        xls: 'application/vnd.ms-excel',
        xlsx: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
        csv: 'text/csv',
        ods: 'application/vnd.oasis.opendocument.spreadsheet',
    
        ppt: 'application/vnd.ms-powerpoint',
        pptx: 'application/vnd.openxmlformats-officedocument.presentationml.presentation',
        odp: 'application/vnd.oasis.opendocument.presentation',
    
        txt: 'text/plain',
        md: 'text/markdown',
        json: 'application/json',
        xml: 'application/xml',
        html: 'text/html',
        js: 'application/javascript',
        ts: 'application/typescript',
        css: 'text/css',
    
        zip: 'application/zip',
        rar: 'application/vnd.rar',
        '7z': 'application/x-7z-compressed',
        tar: 'application/x-tar',
        gz: 'application/gzip',
    
        jpg: 'image/jpeg',
        jpeg: 'image/jpeg',
        png: 'image/png',
        gif: 'image/gif',
        svg: 'image/svg+xml',
        bmp: 'image/bmp',
        tiff: 'image/tiff',
        webp: 'image/webp',
    
        mp3: 'audio/mpeg',
        wav: 'audio/wav',
        mp4: 'video/mp4',
        mov: 'video/quicktime',
        avi: 'video/x-msvideo',
        mkv: 'video/x-matroska',
    };
    
    const fileExtensionToIconMap: Record<string, ReactNode> = {
        pdf: <TbFileTypePdf size='0.75rem' style={{ verticalAlign: '-0.1rem', color: token.colorTextTertiary }} />,
        doc: <TbFileTypeDocx size='0.75rem' style={{ verticalAlign: '-0.1rem', color: token.colorTextTertiary }} />,
        docx: <TbFileTypeDocx size='0.75rem' style={{ verticalAlign: '-0.1rem', color: token.colorTextTertiary }} />,
        rtf: <TbFileTypeTxt size='0.75rem' style={{ verticalAlign: '-0.1rem', color: token.colorTextTertiary }} />,
        odt: <TbFileTypeTxt size='0.75rem' style={{ verticalAlign: '-0.1rem', color: token.colorTextTertiary }} />,
    
        xls: <TbFileTypeXls size='0.75rem' style={{ verticalAlign: '-0.1rem', color: token.colorTextTertiary }} />,
        xlsx: <TbFileTypeXls size='0.75rem' style={{ verticalAlign: '-0.1rem', color: token.colorTextTertiary }} />,
        csv: <TbFileTypeCsv size='0.75rem' style={{ verticalAlign: '-0.1rem', color: token.colorTextTertiary }} />,
        ods: <TbFileTypeXls size='0.75rem' style={{ verticalAlign: '-0.1rem', color: token.colorTextTertiary }} />,
    
        ppt: <TbFileTypePpt size='0.75rem' style={{ verticalAlign: '-0.1rem', color: token.colorTextTertiary }} />,
        pptx: <TbFileTypePpt size='0.75rem' style={{ verticalAlign: '-0.1rem', color: token.colorTextTertiary }} />,
        odp: <TbFileTypePpt size='0.75rem' style={{ verticalAlign: '-0.1rem', color: token.colorTextTertiary }} />,
    
        txt: <TbFileTypeTxt size='0.75rem' style={{ verticalAlign: '-0.1rem', color: token.colorTextTertiary }} />,
        md: <TbMarkdown size='0.75rem' style={{ verticalAlign: '-0.1rem', color: token.colorTextTertiary }} />,
        json: <TbBraces size='0.75rem' style={{ verticalAlign: '-0.1rem', color: token.colorTextTertiary }} />,
        xml: <TbFileTypeXml size='0.75rem' style={{ verticalAlign: '-0.1rem', color: token.colorTextTertiary }} />,
        html: <TbFileTypeHtml size='0.75rem' style={{ verticalAlign: '-0.1rem', color: token.colorTextTertiary }} />,
        js: <TbFileTypeJs size='0.75rem' style={{ verticalAlign: '-0.1rem', color: token.colorTextTertiary }} />,
        ts: <TbFileTypeTs size='0.75rem' style={{ verticalAlign: '-0.1rem', color: token.colorTextTertiary }} />,
        css: <TbFileTypeCss size='0.75rem' style={{ verticalAlign: '-0.1rem', color: token.colorTextTertiary }} />,
    
        zip: <TbFileTypeZip size='0.75rem' style={{ verticalAlign: '-0.1rem', color: token.colorTextTertiary }} />,
        rar: <TbFileTypeZip size='0.75rem' style={{ verticalAlign: '-0.1rem', color: token.colorTextTertiary }} />,
        '7z': <TbFileTypeZip size='0.75rem' style={{ verticalAlign: '-0.1rem', color: token.colorTextTertiary }} />,
        tar: <TbFileTypeZip size='0.75rem' style={{ verticalAlign: '-0.1rem', color: token.colorTextTertiary }} />,
        gz: <TbFileTypeZip size='0.75rem' style={{ verticalAlign: '-0.1rem', color: token.colorTextTertiary }} />,
    
        jpg: <TbFileTypeJpg size='0.75rem' style={{ verticalAlign: '-0.1rem', color: token.colorTextTertiary }} />,
        jpeg: <TbFileTypeJpg size='0.75rem' style={{ verticalAlign: '-0.1rem', color: token.colorTextTertiary }} />,
        png: <TbFileTypePng size='0.75rem' style={{ verticalAlign: '-0.1rem', color: token.colorTextTertiary }} />,
        gif: (
            <svg
                xmlns='http://www.w3.org/2000/svg'
                width='0.75rem'
                height='0.75rem'
                style={{ verticalAlign: '-0.1rem', color: token.colorTextTertiary }}
                viewBox='0 0 24 24'
                fill='none'
                stroke='currentColor'
                strokeWidth='2'
                strokeLinecap='round'
                strokeLinejoin='round'
                className='icon icon-tabler icons-tabler-outline icon-tabler-gif'
            >
                <path stroke='none' d='M0 0h24v24H0z' fill='none' />
                <path d='M8 8h-2a2 2 0 0 0 -2 2v4a2 2 0 0 0 2 2h2v-4h-1' />
                <path d='M12 8v8' />
                <path d='M16 12h3' />
                <path d='M20 8h-4v8' />
            </svg>
        ),
        svg: <TbFileTypeSvg size='0.75rem' style={{ verticalAlign: '-0.1rem', color: token.colorTextTertiary }} />,
        tiff: <TbFileText size='0.75rem' style={{ verticalAlign: '-0.1rem', color: token.colorTextTertiary }} />,
        webp: <TbFileText size='0.75rem' style={{ verticalAlign: '-0.1rem', color: token.colorTextTertiary }} />,
    
        gdoc: <img alt='Google Docs' src={GoogleDocsSVGIcon} style={{ ...imgLogoStyling, transform: 'scale(0.75)' }} />,
        gsheet: (
            <img
                alt='Google Sheets'
                src={GoogleSheetsSVGIcon}
                style={{ ...imgLogoStyling, transform: 'scale(0.75)' }}
            />
        ),
        gslide: (
            <img
                alt='Google Slides'
                src={GoogleSlidesSVGIcon}
                style={{ ...imgLogoStyling, transform: 'scale(0.75)' }}
            />
        ),
        gform: (
            <img alt='Google Forms' src={GoogleFormsSVGIcon} style={{ ...imgLogoStyling, transform: 'scale(0.75)' }} />
        ),
    };
    
    const mimeTypeToKnownReadableFileTypeMap: Record<string, string> = {
        'application/pdf': 'PDF',
        'application/msword': 'Microsoft Word',
        'application/vnd.openxmlformats-officedocument.wordprocessingml.document': 'Microsoft Word',
        'application/rtf': 'Rich Text Format',
        'application/vnd.oasis.opendocument.text': 'OpenDocument Text',
    
        'application/vnd.ms-excel': 'Microsoft Excel',
        'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet': 'Microsoft Excel',
        'text/csv': 'CSV',
        'application/vnd.oasis.opendocument.spreadsheet': 'OpenDocument Spreadsheet',
    
        'application/vnd.ms-powerpoint': 'Microsoft PowerPoint',
        'application/vnd.openxmlformats-officedocument.presentationml.presentation': 'Microsoft PowerPoint',
        'application/vnd.oasis.opendocument.presentation': 'OpenDocument Presentation',
    
        'text/plain': 'Plain Text',
        'text/markdown': 'Markdown',
        'application/json': 'JSON',
        'application/xml': 'XML',
        'text/html': 'HTML',
        'application/javascript': 'JavaScript',
        'application/typescript': 'TypeScript',
        'text/css': 'CSS',
    
        'application/zip': 'ZIP Archive',
        'application/vnd.rar': 'RAR Archive',
        'application/x-7z-compressed': '7-Zip Archive',
        'application/x-tar': 'TAR Archive',
        'application/gzip': 'GZIP Archive',
    
        'image/jpeg': 'JPEG Image',
        'image/png': 'PNG Image',
        'image/gif': 'GIF Image',
        'image/svg+xml': 'SVG Image',
        'image/bmp': 'Bitmap Image',
        'image/tiff': 'TIFF Image',
        'image/webp': 'WebP Image',
    
        'audio/mpeg': 'MP3 Audio',
        'audio/wav': 'WAV Audio',
        'video/mp4': 'MP4 Video',
        'video/quicktime': 'QuickTime Video',
        'video/x-msvideo': 'AVI Video',
        'video/x-matroska': 'MKV Video',
    
        'application/vnd.google-apps.document': 'Google Docs',
        'application/vnd.google-apps.spreadsheet': 'Google Sheets',
        'application/vnd.google-apps.presentation': 'Google Slides',
        'application/vnd.google-apps.form': 'Google Forms',
    };
    
    const mimeTypeToIconMap: Record<string, ReactNode> = {
        'application/pdf': (
            <TbFileTypePdf size='0.75rem' style={{ verticalAlign: '-0.1rem', color: token.colorTextTertiary }} />
        ),
        'application/msword': (
            <TbFileTypeDocx size='0.75rem' style={{ verticalAlign: '-0.1rem', color: token.colorTextTertiary }} />
        ),
        'application/vnd.openxmlformats-officedocument.wordprocessingml.document': (
            <TbFileTypeDocx size='0.75rem' style={{ verticalAlign: '-0.1rem', color: token.colorTextTertiary }} />
        ),
        'application/rtf': (
            <TbFileTypeTxt size='0.75rem' style={{ verticalAlign: '-0.1rem', color: token.colorTextTertiary }} />
        ),
        'application/vnd.oasis.opendocument.text': (
            <TbFileTypeTxt size='0.75rem' style={{ verticalAlign: '-0.1rem', color: token.colorTextTertiary }} />
        ),
    
        'application/vnd.ms-excel': (
            <TbFileTypeXls size='0.75rem' style={{ verticalAlign: '-0.1rem', color: token.colorTextTertiary }} />
        ),
        'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet': (
            <TbFileTypeXls size='0.75rem' style={{ verticalAlign: '-0.1rem', color: token.colorTextTertiary }} />
        ),
        'text/csv': (
            <TbFileTypeCsv size='0.75rem' style={{ verticalAlign: '-0.1rem', color: token.colorTextTertiary }} />
        ),
        'application/vnd.oasis.opendocument.spreadsheet': (
            <TbFileTypeXls size='0.75rem' style={{ verticalAlign: '-0.1rem', color: token.colorTextTertiary }} />
        ),
    
        'application/vnd.ms-powerpoint': (
            <TbFileTypePpt size='0.75rem' style={{ verticalAlign: '-0.1rem', color: token.colorTextTertiary }} />
        ),
        'application/vnd.openxmlformats-officedocument.presentationml.presentation': (
            <TbFileTypePpt size='0.75rem' style={{ verticalAlign: '-0.1rem', color: token.colorTextTertiary }} />
        ),
        'application/vnd.oasis.opendocument.presentation': (
            <TbFileTypePpt size='0.75rem' style={{ verticalAlign: '-0.1rem', color: token.colorTextTertiary }} />
        ),
    
        'text/plain': (
            <TbFileTypeTxt size='0.75rem' style={{ verticalAlign: '-0.1rem', color: token.colorTextTertiary }} />
        ),
        'text/markdown': (
            <TbMarkdown size='0.75rem' style={{ verticalAlign: '-0.1rem', color: token.colorTextTertiary }} />
        ),
        'application/json': (
            <TbBraces size='0.75rem' style={{ verticalAlign: '-0.1rem', color: token.colorTextTertiary }} />
        ),
        'application/xml': (
            <TbFileTypeXml size='0.75rem' style={{ verticalAlign: '-0.1rem', color: token.colorTextTertiary }} />
        ),
        'text/html': (
            <TbFileTypeHtml size='0.75rem' style={{ verticalAlign: '-0.1rem', color: token.colorTextTertiary }} />
        ),
        'application/javascript': (
            <TbFileTypeJs size='0.75rem' style={{ verticalAlign: '-0.1rem', color: token.colorTextTertiary }} />
        ),
        'application/typescript': (
            <TbFileTypeTs size='0.75rem' style={{ verticalAlign: '-0.1rem', color: token.colorTextTertiary }} />
        ),
        'text/css': (
            <TbFileTypeCss size='0.75rem' style={{ verticalAlign: '-0.1rem', color: token.colorTextTertiary }} />
        ),
    
        'application/zip': (
            <TbFileTypeZip size='0.75rem' style={{ verticalAlign: '-0.1rem', color: token.colorTextTertiary }} />
        ),
        'application/vnd.rar': (
            <TbFileTypeZip size='0.75rem' style={{ verticalAlign: '-0.1rem', color: token.colorTextTertiary }} />
        ),
        'application/x-7z-compressed': (
            <TbFileTypeZip size='0.75rem' style={{ verticalAlign: '-0.1rem', color: token.colorTextTertiary }} />
        ),
        'application/x-tar': (
            <TbFileTypeZip size='0.75rem' style={{ verticalAlign: '-0.1rem', color: token.colorTextTertiary }} />
        ),
        'application/gzip': (
            <TbFileTypeZip size='0.75rem' style={{ verticalAlign: '-0.1rem', color: token.colorTextTertiary }} />
        ),
    
        'image/jpeg': (
            <TbFileTypeJpg size='0.75rem' style={{ verticalAlign: '-0.1rem', color: token.colorTextTertiary }} />
        ),
        'image/png': (
            <TbFileTypePng size='0.75rem' style={{ verticalAlign: '-0.1rem', color: token.colorTextTertiary }} />
        ),
        'image/gif': (
            <svg
                xmlns='http://www.w3.org/2000/svg'
                width='0.75rem'
                height='0.75rem'
                style={{ verticalAlign: '-0.1rem', color: token.colorTextTertiary }}
                viewBox='0 0 24 24'
                fill='none'
                stroke='currentColor'
                strokeWidth='2'
                strokeLinecap='round'
                strokeLinejoin='round'
                className='icon icon-tabler icons-tabler-outline icon-tabler-gif'
            >
                <path stroke='none' d='M0 0h24v24H0z' fill='none' />
                <path d='M8 8h-2a2 2 0 0 0 -2 2v4a2 2 0 0 0 2 2h2v-4h-1' />
                <path d='M12 8v8' />
                <path d='M16 12h3' />
                <path d='M20 8h-4v8' />
            </svg>
        ),
        'image/svg+xml': (
            <TbFileTypeSvg size='0.75rem' style={{ verticalAlign: '-0.1rem', color: token.colorTextTertiary }} />
        ),
        'image/tiff': (
            <TbFileText size='0.75rem' style={{ verticalAlign: '-0.1rem', color: token.colorTextTertiary }} />
        ),
        'image/webp': (
            <TbFileText size='0.75rem' style={{ verticalAlign: '-0.1rem', color: token.colorTextTertiary }} />
        ),
    
        'application/vnd.google-apps.document': (
            <img alt='Google Docs' src={GoogleDocsSVGIcon} style={{ ...imgLogoStyling, transform: 'scale(0.75)' }} />
        ),
        'application/vnd.google-apps.spreadsheet': (
            <img
                alt='Google Sheets'
                src={GoogleSheetsSVGIcon}
                style={{ ...imgLogoStyling, transform: 'scale(0.75)' }}
            />
        ),
        'application/vnd.google-apps.presentation': (
            <img
                alt='Google Slides'
                src={GoogleSlidesSVGIcon}
                style={{ ...imgLogoStyling, transform: 'scale(0.75)' }}
            />
        ),
        'application/vnd.google-apps.form': (
            <img alt='Google Forms' src={GoogleFormsSVGIcon} style={{ ...imgLogoStyling, transform: 'scale(0.75)' }} />
        ),
    };

    return {
        fileExtensionToMimeTypeMap,
        fileExtensionToIconMap,
        mimeTypeToKnownReadableFileTypeMap,
        mimeTypeToIconMap,
    };
};
